import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const PRESETS = [
    "Affitto",
    "Utenze",
    "Stipendi",
    "Tasse",
    "Manutenzione",
    "Marketing",
    "Servizi",
    "Forniture",
    "Assicurazioni",
    "Commercialista",
];

const COLORS = [
    "#002FA7",
    "#FF2A00",
    "#00C853",
    "#FFB300",
    "#9C27B0",
    "#00BCD4",
    "#795548",
    "#607D8B",
];

export default function Categories() {
    const { agencyId, user } = useAuth();
    const [cats, setCats] = useState([]);
    const [breakdown, setBreakdown] = useState([]);
    const [form, setForm] = useState({ name: "", color: COLORS[0] });
    const [confirmDel, setConfirmDel] = useState(null);
    const canEdit = ["owner", "agency_manager"].includes(user?.role);

    const load = () => {
        if (!agencyId) return;
        api.get(`/expense-categories`, { params: { agency_id: agencyId } }).then((r) =>
            setCats(r.data),
        );
        api.get(`/reports/expense-by-category`, {
            params: { agency_id: agencyId, period: "monthly" },
        }).then((r) => setBreakdown(r.data));
    };
    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const create = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) return;
        try {
            await api.post("/expense-categories", {
                agency_id: agencyId,
                name: form.name.trim(),
                color: form.color,
            });
            toast.success("Categoria creata");
            setForm({ name: "", color: COLORS[0] });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const createFromPreset = async (name) => {
        try {
            await api.post("/expense-categories", {
                agency_id: agencyId,
                name,
                color: COLORS[Math.floor(Math.random() * COLORS.length)],
            });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/expense-categories/${confirmDel.id}`);
            toast.success("Categoria eliminata");
            setConfirmDel(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const totalMonth = breakdown.reduce((s, b) => s + b.total, 0);
    const missing = PRESETS.filter((p) => !cats.some((c) => c.name.toLowerCase() === p.toLowerCase()));

    return (
        <div data-testid="categories-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Impostazioni</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Categorie Spese
                    </h1>
                </div>
            </div>

            <div className="mb-8 p-4 bg-white border border-zinc-200 text-sm text-zinc-700">
                Le categorie ti permettono di classificare le <strong>uscite</strong> e
                vedere il <strong>P&L dettagliato</strong>. Quando registri un'uscita
                puoi scegliere una categoria dal menu a discesa.
            </div>

            {canEdit && (
                <form
                    onSubmit={create}
                    className="bg-white border border-zinc-200 p-6 mb-8"
                    data-testid="category-form"
                >
                    <div className="overline mb-3">Nuova categoria</div>
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                        <label className="block">
                            <div className="overline mb-2">Nome</div>
                            <input
                                className="inp"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="es. Affitto, Utenze"
                                data-testid="category-name-input"
                            />
                        </label>
                        <div>
                            <div className="overline mb-2">Colore</div>
                            <div className="flex gap-1">
                                {COLORS.map((c) => (
                                    <button
                                        type="button"
                                        key={c}
                                        onClick={() => setForm({ ...form, color: c })}
                                        className="w-8 h-8 border-2"
                                        style={{
                                            background: c,
                                            borderColor: form.color === c ? "#09090b" : "transparent",
                                        }}
                                        aria-label={`Colore ${c}`}
                                    />
                                ))}
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="btn-primary flex items-center gap-2"
                            data-testid="category-submit"
                        >
                            <Plus size={14} weight="bold" /> Aggiungi
                        </button>
                    </div>
                    {missing.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-zinc-100">
                            <div className="overline mb-2">Preset rapidi</div>
                            <div className="flex flex-wrap gap-2">
                                {missing.map((m) => (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => createFromPreset(m)}
                                        className="px-3 py-1 text-xs font-bold uppercase tracking-wider border border-zinc-200 hover:border-ink hover:bg-zinc-50"
                                    >
                                        + {m}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </form>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline">
                        Categorie ({cats.length})
                    </div>
                    {cats.length === 0 ? (
                        <div className="p-12 text-center text-zinc-500">
                            Nessuna categoria ancora. Creala con il form sopra.
                        </div>
                    ) : (
                        <ul>
                            {cats.map((c) => (
                                <li
                                    key={c.id}
                                    className="flex items-center justify-between px-6 py-3 border-b border-zinc-100 last:border-0"
                                    data-testid={`category-row-${c.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span
                                            className="w-3 h-3 border border-zinc-200"
                                            style={{ background: c.color }}
                                        />
                                        <span className="font-bold">{c.name}</span>
                                    </div>
                                    {canEdit && (
                                        <button
                                            onClick={() => setConfirmDel(c)}
                                            className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-red-600 hover:bg-zinc-100"
                                            data-testid={`delete-category-${c.id}`}
                                            aria-label="Elimina categoria"
                                        >
                                            <Trash size={14} weight="bold" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-4 border-b border-zinc-200 overline">
                        Spese Mese Corrente · Totale {euro(totalMonth)}
                    </div>
                    {breakdown.length === 0 ? (
                        <div className="p-12 text-center text-zinc-500">
                            Nessuna spesa nel mese.
                        </div>
                    ) : (
                        <ul data-testid="expense-breakdown">
                            {breakdown.map((b) => {
                                const pct = totalMonth > 0 ? (b.total / totalMonth) * 100 : 0;
                                const cat = cats.find((c) => c.name === b.category);
                                return (
                                    <li
                                        key={b.category}
                                        className="px-6 py-3 border-b border-zinc-100 last:border-0"
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="flex items-center gap-2 text-sm">
                                                <span
                                                    className="w-3 h-3 border border-zinc-200"
                                                    style={{
                                                        background: cat?.color || "#d4d4d8",
                                                    }}
                                                />
                                                <span className="font-bold">{b.category}</span>
                                                <span className="text-xs text-zinc-500">
                                                    ({b.count})
                                                </span>
                                            </span>
                                            <span className="tabular-nums font-bold">
                                                {euro(b.total)}
                                            </span>
                                        </div>
                                        <div className="h-1 bg-zinc-100">
                                            <div
                                                className="h-full"
                                                style={{
                                                    width: `${pct}%`,
                                                    background: cat?.color || "#71717a",
                                                }}
                                            />
                                        </div>
                                        <div className="text-xs text-zinc-500 mt-1 tabular-nums">
                                            {pct.toFixed(1)}%
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>

            {confirmDel && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setConfirmDel(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                    >
                        <div className="overline mb-3">Eliminare categoria?</div>
                        <h2 className="text-2xl font-black tracking-tighter mb-4">
                            {confirmDel.name}
                        </h2>
                        <p className="text-sm text-zinc-700 mb-6">
                            Le uscite già registrate con questa categoria manterranno
                            l'etichetta, ma non potrai più selezionarla nei nuovi inserimenti.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmDel(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button onClick={remove} className="btn-destructive flex-1">
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
